import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const DashboardTutorial = ({ isOpen, onClose, onComplete, className = '' }) => {
  const intl = useIntl();

  const steps = useMemo(() => [
    {
      id: 'value-maturity-index',
      title: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_vmi_title' }),
      description: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_vmi_desc' }),
      targetSelector: '[data-pol-id="ojlh1p"]',
      position: 'right',
    },
    {
      id: 'readiness-attractiveness',
      title: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_matrix_title' }),
      description: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_matrix_desc' }),
      targetSelector: '[data-pol-id="nsbuyx"]',
      position: 'left',
    },
    {
      id: 'profit-gap',
      title: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_bell_title' }),
      description: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_bell_desc' }),
      targetSelector: '[data-pol-id="wbpeig"]',
      position: 'left',
    },
    {
      id: 'progress-cards',
      title: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_progress_title' }),
      description: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_progress_desc' }),
      targetSelector: '[data-pol-id="wtlxx6"]',
      position: 'top',
    },
    {
      id: 'current-sprint',
      title: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_sprint_title' }),
      description: intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_sprint_desc' }),
      targetSelector: '[data-pol-id="i6vme4"]',
      position: 'top',
    },
  ], [intl]);

  const [current, setCurrent] = useState(0);
  const [highlightEl, setHighlightEl] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const isLast = current === steps.length - 1;
  const isFirst = current === 0;
  const step = steps[current];

  const computePosition = (rect, position, viewportWidth, viewportHeight) => {
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    let top = 0;
    let left = 0;
    switch (position) {
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + 20;
        if (left + tooltipWidth > viewportWidth) left = rect.left - tooltipWidth - 20;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - 20;
        if (left < 0) left = rect.right + 20;
        break;
      case 'top':
        top = rect.top - tooltipHeight - 20;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        if (top < 0) top = rect.bottom + 20;
        if (left < 0) left = 20;
        if (left + tooltipWidth > viewportWidth) left = viewportWidth - tooltipWidth - 20;
        break;
      case 'bottom':
      default:
        top = rect.bottom + 20;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        if (top + tooltipHeight > viewportHeight) top = rect.top - tooltipHeight - 20;
        if (left < 0) left = 20;
        if (left + tooltipWidth > viewportWidth) left = viewportWidth - tooltipWidth - 20;
        break;
    }
    top = Math.max(20, Math.min(top, viewportHeight - tooltipHeight - 20));
    left = Math.max(20, Math.min(left, viewportWidth - tooltipWidth - 20));
    return { top, left };
  };

  useEffect(() => {
    if (!isOpen) return;

    let rafId = null;
    let retryTimer = null;
    let mutationObserver = null;
    let scrollParents = [];

    const isScrollable = (node) => {
      if (!node || node === document.body) return false;
      const style = window.getComputedStyle(node);
      const oy = style.overflowY;
      return oy === 'auto' || oy === 'scroll';
    };

    const getScrollParents = (el) => {
      const parents = [];
      let node = el?.parentElement;
      while (node) {
        if (isScrollable(node)) parents.push(node);
        node = node.parentElement;
      }
      parents.push(window);
      return parents;
    };

    const positionNow = () => {
      if (!highlightEl) return;
      const rect = highlightEl.getBoundingClientRect();
      const vpW = window.innerWidth;
      const vpH = window.innerHeight;
      setTooltipPos(computePosition(rect, step.position, vpW, vpH));
    };

    const attachScrollListeners = (el) => {
      scrollParents = getScrollParents(el);
      scrollParents.forEach((p) => p.addEventListener('scroll', positionNow, { passive: true }));
    };

    const detachScrollListeners = () => {
      scrollParents.forEach((p) => p.removeEventListener('scroll', positionNow));
      scrollParents = [];
    };

    const findAndHighlight = (attempts = 30) => {
      const el = step ? document.querySelector(step.targetSelector) : null;
      if (el && el.getBoundingClientRect().width > 0 && el.getBoundingClientRect().height > 0) {
        setHighlightEl(el);
        // next frame to ensure layout paint
        rafId = window.requestAnimationFrame(() => {
          positionNow();
          try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch (_) {}
          detachScrollListeners();
          attachScrollListeners(el);
        });
      } else if (attempts > 0) {
        retryTimer = window.setTimeout(() => findAndHighlight(attempts - 1), 120);
      }
    };

    // Initial pass with a tiny delay to allow child components (charts) to mount
    retryTimer = window.setTimeout(() => findAndHighlight(30), 100);

    const onResize = () => positionNow();
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);

    // Watch DOM changes to re-position when tiles finish rendering
    mutationObserver = new MutationObserver(() => {
      // Debounce with rAF
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(positionNow);
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (retryTimer) clearTimeout(retryTimer);
      if (mutationObserver) mutationObserver.disconnect();
      detachScrollListeners();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
    };
  }, [current, step, isOpen, highlightEl]);

  useEffect(() => {
    const orig = document.body.style.overflow;
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50`}>
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        {highlightEl && (
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              top: highlightEl.getBoundingClientRect().top - 8,
              left: highlightEl.getBoundingClientRect().left - 8,
              width: highlightEl.offsetWidth + 16,
              height: highlightEl.offsetHeight + 16,
              boxShadow: '0 0 0 4px rgba(59,130,246,0.5), 0 0 0 9999px rgba(0,0,0,0.5)',
              borderRadius: 12,
              transition: 'all 200ms ease',
            }}
          />
        )}
      </div>

      <div
        className={`fixed z-50 bg-white rounded-xl shadow-2xl border border-succession-border p-6 max-w-sm ${className}`}
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-succession-primary bg-succession-primary-10 px-2 py-1 rounded-full">
                {intl.formatMessage({ id: 'succession.value_acceleration.tutorial.step_of' }, { current: current + 1, total: steps.length })}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-succession-foreground">{step?.title}</h3>
          </div>
          <button onClick={() => { setCurrent(0); setHighlightEl(null); onClose?.(); }} className="p-1 hover:bg-succession-primary-10 rounded-lg ml-2" aria-label={intl.formatMessage({ id: 'succession.value_acceleration.tutorial.close_label' })}>
            <svg className="w-5 h-5 text-succession-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <p className="text-succession-muted mb-6 leading-relaxed">{step?.description}</p>

        <div className="flex items-center justify-between">
          <button
            onClick={() => { if (!isFirst) setCurrent(c => Math.max(0, c - 1)); }}
            disabled={isFirst}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${isFirst ? 'text-succession-muted cursor-not-allowed' : 'text-succession-foreground hover:bg-succession-primary-10'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            {intl.formatMessage({ id: 'succession.value_acceleration.tutorial.previous' })}
          </button>

          <div className="flex gap-1">
            {steps.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full ${i === current ? 'bg-succession-primary' : 'bg-succession-border'}`} />
            ))}
          </div>

          <button
            onClick={() => {
              if (isLast) { onComplete?.(); onClose?.(); }
              else setCurrent(c => Math.min(steps.length - 1, c + 1));
            }}
            className="flex items-center gap-2 px-4 py-2 bg-succession-primary text-white rounded-lg font-medium hover:bg-succession-primary/90"
          >
            {isLast ? (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                {intl.formatMessage({ id: 'succession.value_acceleration.tutorial.finish' })}
              </>
            ) : (
              <>
                {intl.formatMessage({ id: 'succession.value_acceleration.tutorial.next' })}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default DashboardTutorial;


